import TorusCanvas from "components/pages/home/TorusCanvas"

/**
 * Ambient variant of the TorusCanvas used on the home page hero.
 *
 * - Blue hue matching the big-stone section background
 * - Way fewer particles, slower drift, quieter dots (reads as texture, not data)
 * - More frequent + longer-lived pulse rings (the part we want to feature)
 * - Nudges the torus off-center so the "hole" isn't sitting front-and-center
 */
function AmbientTorusCanvas({ className = "", style = {} }) {
  return (
    <TorusCanvas
      className={className}
      style={{ background: "transparent", ...style }}
      hue={212}
      background="transparent"
      settingsOverrides={{
        count: 2000,
        baseCount: 2000,
        // Bigger torus to fill the hero canvas all the way to the corners.
        majorRadius: 380,
        // Single torus — drop the near/foreground one entirely.
        nearTorusAlpha: 0,
        // Thinner tube so the torus reads as an orbital ring / galaxy arm
        // rather than a chunky donut.
        tubeRadius: 36,
        // Straighter lines — the far-away wiggle was the most distracting
        // part, so these are cut hard.
        bendAmplitude: 0.5,
        fuzz: 0.75,
        // Slow the whole particle flow, not just the data pulses.
        // Reads more like a drifting starfield than a humming ring.
        speed: 6,
        // dataSpeed controls both pulse travel speed AND how often a pulse
        // reappears on each strand (one pulse per strand, looping), so 2x
        // here = 2x faster AND 2x more frequent.
        dataSpeed: 0.08,
        torusSpinSpeed: 0.012,
        cameraDriftSpeedY: 0.00008,
        cameraDriftSpeedX: 0.00004,
        // Milky-Way-style tilt — look across the plane of the torus.
        // Uses viewTiltOffset* because the base canvas rewrites
        // viewTiltX/Y every frame from the camera drift oscillation.
        viewTiltOffsetX: 1.1,
        viewTiltOffsetY: -0.24,
        // Pulse rings — beefed-up but not overwhelming. Roughly
        // halfway between the original ambient preset and the
        // "fill the canvas" pass.
        rippleTriggerChance: 0.16,
        rippleDuration: 5.5,
        rippleMaxRadiusScale: 32,
        rippleMaxAlpha: 0.6,
        rippleLineWidth: 1.2,
        rippleLineWidthMin: 0.3,
        // Everything drawn should sit ABOVE the big-stone (~18% L) bg —
        // boost lightness so trails read as lighter-than-background
        // rather than darker streaks.
        lineLightnessBoost: 32,
        dotMaxAlpha: 0.22,
        dotBaseLightnessBoost: 14,
        dotLightnessBoost: 20,
        dataDotLightnessBoost: 28,
        // Kill the soft glow halo around data pulses — crisper, no blur.
        pulseGlowOpacityBoost: 0,
        pulseGlowLightnessBoost: 0,
        pulseGlowWidthScale: 1,
        pulseGlowLengthScale: 1,
        // Nearly flatten the depth-based falloff: saturation almost
        // uniform, lightness drop a fraction of default, and the alpha
        // floor lifted most of the way to fully opaque. Far side of
        // the torus reads just about as clearly as the near side.
        farDepthSaturationScale: 0.945,
        farDepthLightnessDrop: 1.875,
        depthAlphaFloor: 0.77,
      }}
      adaptiveOverrides={{
        minCount: 800,
        maxCount: 3000,
        desiredCount: 2000,
      }}
    />
  )
}

export default AmbientTorusCanvas
